import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import $ from 'jquery';
import { isEqual } from 'lodash';
import { FlushTabs, type FlushTabsProps } from './FlushTabs';
import { type Tab } from './types';
import {
  AhaButton,
  AhaMenu,
  AhaMenuContent,
  AhaMenuItem,
} from 'components/common/reactifyWebComponent';

const DEFAULT_MENU_WIDTH = 32;

// This is a really gross hack. The problem is that the `tabs` are now dynamic and can change based on the status
// of a record. Previously the tabs were completely static, and might only change order. Finding out that the current
// activeTab is no longer visible is relatively simple, but the challenge was properly updating the browser with the
// first active tab. Updating the React component is simple, but the tab conents lives outside of the scope of this
// component, and the active tab _pane_ is controlled via the standard Bootstrap Tab JS. Keeping both in-sync is a
// little challenging now that tabs can be added/removed via a reactive update. This seems like the least hacky way
// to fix all of the UX challenges.
function resetActiveTab(container: HTMLElement | null, tabKey: string) {
  if (!container) {
    return;
  }

  const tabButton = container.querySelector<HTMLButtonElement>(
    `.flush-tabs__button[data-tab-key="${tabKey}"]`
  );
  if (tabButton) {
    tabButton.click();
  } else {
    // We need to wait for the tab button to be rendered
    requestAnimationFrame(() => resetActiveTab(container, tabKey));
  }
}

type DynamicTabsProps = {
  initialActiveKey?: string;
  initialVisibleTabCount?: number;
  prefix: string;
  tabs: Tab[];
  useLinks?: boolean;
  onSelect?: (tab: Tab) => void;
};

type DynamicTabsPassThroughProps = Omit<
  FlushTabsProps,
  'activeKey' | 'className' | 'id' | 'onSelect'
>;

interface TabWidths {
  _total?: number;
}

export const DynamicTabs: React.FC<
  DynamicTabsProps & DynamicTabsPassThroughProps
> = ({
  initialActiveKey = '',
  initialVisibleTabCount = 4,
  prefix = '',
  tabs,
  useLinks = false,
  onSelect = null,
  renderTab = tab => tab.title,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver>();
  // If you have ventured down a deep path to find the url activated by a tab,
  // this is not the place. Your journey takes you to the actual tab contents.
  const [activeKey, setActiveKey] = useState(initialActiveKey || tabs[0].key);
  const [offsetWidth, setOffsetWidth] = useState(null);
  const [tabWidths, setTabWidths] = useState<TabWidths>({});
  const [currentTabs, setCurrentTabs] = useState(tabs);

  useLayoutEffect(() => {
    const activeTab = tabs.find(tab => tab.key === activeKey);
    if (!activeTab) {
      resetActiveTab(containerRef.current, tabs[0].key);
    }
  }, [tabs, activeKey]);

  // Calculate the widths of each tab
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const newTabWidths = { _total: 0 };
    $(containerRef.current)
      .find('.flush-tabs__tab')
      .each((_, tab) => {
        // Add 1px to width for sub-pixel widths
        newTabWidths._total += newTabWidths[tab.dataset.tabKey as string] =
          Math.ceil($(tab).outerWidth(true) ?? 0) + 1;
      });

    if (!isEqual(tabWidths, newTabWidths)) {
      setTabWidths(newTabWidths);
    }
  }, [tabWidths, currentTabs]);

  // Calculate the width of the tabs, and then re-order them if we need to force the active tab to be visible
  useEffect(() => {
    if (offsetWidth === null || !tabWidths._total) {
      return;
    }

    const menuWidth =
      (menuRef.current && $(menuRef.current).width()) || DEFAULT_MENU_WIDTH;
    const containerWidth = offsetWidth - menuWidth;

    if (tabWidths._total <= containerWidth) {
      setCurrentTabs(tabs);
    } else {
      // if no active tab, assume it's gone, so the first tab will become active in the useLayoutEffect above
      const activeTab = tabs.find(tab => tab.key === activeKey) ?? tabs[0];
      const orderedTabs = [activeTab];
      let currentWidth = tabWidths[activeTab.key];
      let addTabsToEnd = false;

      // Add as many tabs that will fit in front of the activeTab
      for (let i = 0; i < tabs.length; i++) {
        let tab = tabs[i];

        if (tab === activeTab) {
          addTabsToEnd = true;
          continue;
        }

        const tabWidth = tabWidths[tab.key];
        currentWidth += tabWidth;

        tab = { ...tab, hidden: currentWidth >= containerWidth };

        if (addTabsToEnd) {
          orderedTabs.push(tab);
        } else {
          orderedTabs.splice(orderedTabs.length - 1, 0, tab);
        }
      }

      setCurrentTabs(orderedTabs);
    }
  }, [activeKey, tabs, offsetWidth, tabWidths]);

  // Create our ResizeObserver
  useEffect(() => {
    if (
      resizeObserverRef.current ||
      !containerRef.current ||
      !window.ResizeObserver
    ) {
      return;
    }

    const observer = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width;
      setOffsetWidth(width);
    });

    resizeObserverRef.current = observer;
    observer.observe(containerRef.current);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = undefined;
      }
    };
  }, []);

  const onSelectTab = (tab: Tab) => {
    setActiveKey(tab.key);

    if (onSelect) {
      onSelect(tab);
    }
  };

  const hiddenTabs = currentTabs.filter(tab => tab.hidden);
  let menuContent: React.ReactNode;
  if (hiddenTabs.length) {
    menuContent = hiddenTabs.map(tab => (
      <AhaMenuItem key={tab.key} active={tab.key === activeKey || null}>
        <a
          onClick={() => {
            onSelectTab(tab);
          }}
          data-toggle={useLinks ? null : 'tab'}
          data-tab-key={tab.key}
          href={useLinks ? tab.url : `#${prefix}_tab_${tab.key}`}
        >
          {renderTab(tab, 'menu')}
        </a>
      </AhaMenuItem>
    ));
  }

  return (
    <FlushTabs
      ref={containerRef}
      activeKey={activeKey}
      className='dynamic-tabs'
      id={prefix}
      tabs={currentTabs}
      onSelect={tab => onSelectTab(tab)}
      useLinks={useLinks}
      renderTab={renderTab}
      {...props}
    >
      <AhaMenu
        ref={menuRef}
        style={{ visibility: menuContent ? 'visible' : 'hidden' }}
      >
        <AhaButton slot='control' kind='ghost'>
          <i className='fa-solid fa-ellipsis fa-fw' />
        </AhaButton>

        <AhaMenuContent>{menuContent}</AhaMenuContent>
      </AhaMenu>
    </FlushTabs>
  );
};
